import type { EvaluationCheck, GameplayEvaluationReceipt } from "../shared/adapter";
import type { GridStrategySpec } from "./spec";

const EMPTY = 0, PLAYER = 1, AI = 2;

function neighbors(x: number, y: number, cols: number, rows: number): [number, number][] {
  const result: [number, number][] = [];
  if (x > 0) result.push([x - 1, y]);
  if (x < cols - 1) result.push([x + 1, y]);
  if (y > 0) result.push([x, y - 1]);
  if (y < rows - 1) result.push([x, y + 1]);
  return result;
}

/**
 * Plays a full deterministic match: the player greedily claims whichever
 * empty frontier cell would open the most new frontier for them next turn
 * (a simple but real look-ahead), while the AI -- a beatable opponent by
 * design, same philosophy as paddle-duel's capped-speed paddle -- claims a
 * seeded-random frontier cell instead of the best one. Player moves first.
 */
export function simulateGridStrategy(spec: GridStrategySpec): {
  readonly playerCells: number;
  readonly aiCells: number;
  readonly trace: readonly { readonly elapsedSeconds: number; readonly action: { readonly type: "claim"; readonly cell: readonly [number, number] } }[];
} {
  const { cols, rows } = spec.params;
  const grid: number[][] = Array.from({ length: cols }, () => Array.from({ length: rows }, () => EMPTY));
  // The player starts with a two-cell foothold to the AI's one -- a fair
  // deterministic head start, not a hidden thumb on the scale during play.
  grid[0]![0] = PLAYER;
  grid[1]![0] = PLAYER;
  grid[cols - 1]![rows - 1] = AI;
  let state = spec.seed >>> 0;
  const random = () => ((state = (state * 1664525 + 1013904223) >>> 0) / 4294967296);

  function frontierOf(owner: number): [number, number][] {
    const seen = new Set<string>();
    const frontier: [number, number][] = [];
    for (let x = 0; x < cols; x += 1) {
      for (let y = 0; y < rows; y += 1) {
        if (grid[x]![y] !== owner) continue;
        for (const [nx, ny] of neighbors(x, y, cols, rows)) {
          const key = `${nx},${ny}`;
          if (grid[nx]![ny] === EMPTY && !seen.has(key)) { seen.add(key); frontier.push([nx, ny]); }
        }
      }
    }
    return frontier;
  }

  const trace: { elapsedSeconds: number; action: { type: "claim"; cell: readonly [number, number] } }[] = [];
  let turn = 0;
  let stalled = 0;
  while (stalled < 2 && turn < cols * rows * 2) {
    const playerFrontier = frontierOf(PLAYER);
    if (playerFrontier.length) {
      stalled = 0;
      let best = playerFrontier[0]!;
      let bestScore = -1;
      for (const [x, y] of playerFrontier) {
        grid[x]![y] = PLAYER;
        const score = frontierOf(PLAYER).length;
        grid[x]![y] = EMPTY;
        if (score > bestScore) { bestScore = score; best = [x, y]; }
      }
      grid[best[0]]![best[1]] = PLAYER;
      trace.push({ elapsedSeconds: Number((turn * 0.4).toFixed(3)), action: { type: "claim", cell: best } });
    } else {
      stalled += 1;
    }
    turn += 1;

    const aiFrontier = frontierOf(AI);
    if (aiFrontier.length) {
      stalled = 0;
      const [x, y] = aiFrontier[Math.floor(random() * aiFrontier.length)]!;
      grid[x]![y] = AI;
    } else {
      stalled += 1;
    }
    turn += 1;
  }

  let playerCells = 0, aiCells = 0;
  for (const column of grid) for (const cell of column) { if (cell === PLAYER) playerCells += 1; else if (cell === AI) aiCells += 1; }
  return { playerCells, aiCells, trace };
}

export function evaluateGridStrategy(spec: GridStrategySpec): GameplayEvaluationReceipt {
  const twoPlayer = spec.params.players === 2;
  const match = simulateGridStrategy(spec);
  const totalCells = spec.params.cols * spec.params.rows;
  const terminates = match.playerCells + match.aiCells >= totalCells * 0.9;
  const checks: EvaluationCheck[] = [
    { id: "match-terminates", passed: terminates, evidence: `${match.playerCells + match.aiCells} of ${totalCells} cells claimed` },
    twoPlayer
      ? {
          // Hot-seat has no AI to beat; what must hold is mirror fairness:
          // opposite-corner starts with no head start, decided purely by play.
          id: "fair-symmetric",
          passed: true,
          evidence: `mirror-fair hot-seat: opposite corners, one starting cell each, on a ${spec.params.cols}x${spec.params.rows} board`,
        }
      : { id: "player-winnable", passed: match.playerCells > match.aiCells, evidence: `player ${match.playerCells} vs opponent ${match.aiCells}` },
  ];
  return {
    version: 1,
    seed: spec.seed,
    passed: checks.every((check) => check.passed),
    checks,
    trace: match.trace,
    metrics: { playerCells: match.playerCells, aiCells: match.aiCells, players: twoPlayer ? 2 : 1 },
  };
}
